import { ALABAMA_BIAS, PHOTON_URL, US_STATES } from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import { t } from "../i18n";
import {
  googlePlace,
  googleSuggest,
  pickFromSuggestion,
  type GoogleSuggestion,
} from "../lib/addressSearch";
import { useDebounced } from "../lib/hooks";

export interface AddressPick {
  address: string;
  city: string;
  state: string;
  zip: string;
  lat: number | null;
  lng: number | null;
}

interface Suggestion extends AddressPick {
  /** True when we matched the street but had to reuse the typed house number. */
  approximate: boolean;
}

/**
 * One row in the drop-down: either a Google suggestion, whose city, zip and
 * pin are only fetched when it is picked, or a finished Photon one.
 */
type Row =
  | { kind: "google"; suggestion: GoogleSuggestion }
  | { kind: "photon"; suggestion: Suggestion };

/**
 * Address autocomplete. Google first, through the `address-search` Edge
 * Function, because it knows the house numbers of Shelby and Jefferson County
 * and Photon does not (ADR 0231). When Google is not available — no key yet,
 * or a failed call — it falls back to Photon, described below.
 *
 * Photon/Komoot — free, no API key, biased toward
 * Alabama. Carried over from the prototype including the house-number fallback:
 * Photon often returns the street without the number, so we keep whatever the
 * user typed and flag the suggestion as approximate.
 *
 * The number is also why the box has to ask twice. Photon's index of streets is
 * far better than its index of house numbers, and a query carrying a number it
 * does not know returns *nothing at all* rather than the street it is on —
 * "3265 McMillan Creek Dr Milton" comes back empty while "McMillan Creek Dr
 * Milton" finds it. The house-number fallback below was written for the case
 * that never arrived, because the first search had already returned zero
 * results. So a numbered query that finds nothing is asked again without the
 * number, and the fallback then puts the typed number back.
 */
export function AddressInput({
  value,
  onChange,
  onPick,
  style,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  onPick: (pick: AddressPick) => void;
  style?: React.CSSProperties;
  id?: string;
}) {
  const [suggestions, setSuggestions] = useState<Row[]>([]);
  /** False until a search has come back, so "no match" cannot flash mid-type. */
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(value, 250);
  const latest = useRef(0);
  /**
   * One Google session from the first search to the pick. Google bills the
   * searches in a session as nothing when a pick ends it; a fresh token per
   * keystroke would bill every one.
   */
  const session = useRef<string | null>(null);
  const sessionToken = () => (session.current ??= newSessionToken());
  /**
   * Only what the rep types is searched. The box opens holding the saved
   * address on every job screen, and a pick writes the picked address back
   * into it; searching either would be a paid Google lookup nobody sees.
   */
  const typed = useRef(false);

  useEffect(() => {
    const query = debounced.trim();
    if (!typed.current || query.length < 4) {
      setSuggestions([]);
      setSearched(false);
      return;
    }
    const ticket = ++latest.current;
    // A new query has not found nothing — it has not finished. Without this,
    // "No match on the map" from the misspelling somebody is in the middle of
    // correcting stays on screen through the debounce and both round trips,
    // still insisting there is no such street while the street is being found.
    setSearched(false);

    void (async () => {
      const google = await googleSuggest(query, sessionToken());
      if (google !== null) {
        if (ticket !== latest.current) return;
        setSuggestions(
          google.map((suggestion) => ({ kind: "google", suggestion })),
        );
        setSearched(true);
        return;
      }

      let features = await lookup(query);

      // Nothing, and there is a house number in the way. Ask for the street.
      const withoutNumber = /^\s*\d+\s+(.+)/.exec(query)?.[1];
      if (features.length === 0 && withoutNumber) {
        features = await lookup(withoutNumber);
      }

      if (ticket !== latest.current) return;
      setSuggestions(
        toSuggestions(features, query).map((suggestion) => ({
          kind: "photon",
          suggestion,
        })),
      );
      setSearched(true);
    })();
  }, [debounced]);

  const nothingFound = searched && suggestions.length === 0;

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <input
        id={id}
        value={value}
        style={style}
        autoComplete="off"
        onChange={(e) => {
          typed.current = true;
          onChange(e.target.value);
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && suggestions.length > 0 && (
        <div className="ac-drop">
          {suggestions.map((row, i) =>
            row.kind === "google" ? (
              <div
                key={`${row.suggestion.placeId}|${i}`}
                className="ac-item"
                onMouseDown={() => {
                  const picked = row.suggestion;
                  const token = sessionToken();
                  session.current = null;
                  typed.current = false;
                  setOpen(false);
                  void googlePlace(picked.placeId, token, value).then((pick) =>
                    onPick(pick ?? pickFromSuggestion(picked)),
                  );
                }}
              >
                <div className="an">{row.suggestion.main}</div>
                <div className="aa">{row.suggestion.secondary}</div>
              </div>
            ) : (
              <div
                key={`${row.suggestion.address}|${row.suggestion.city}|${row.suggestion.zip}|${i}`}
                className="ac-item"
                onMouseDown={() => {
                  typed.current = false;
                  onPick(row.suggestion);
                  setOpen(false);
                }}
              >
                <div className="an">
                  {row.suggestion.address}
                  {row.suggestion.approximate && (
                    <span className="na-hint">
                      {" "}
                      {t("(approx. — street match)")}
                    </span>
                  )}
                </div>
                <div className="aa">
                  {[
                    row.suggestion.city,
                    row.suggestion.state,
                    row.suggestion.zip,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              </div>
            ),
          )}
        </div>
      )}
      {/* An empty box looked identical to a box that had not searched yet, so a
          misspelt street read as "the lookup is broken". It is worth saying,
          because the map this searches does not know every new street and the
          address can simply be typed. */}
      {open && nothingFound && (
        <div className="ac-drop">
          <div className="ac-item" style={{ cursor: "default" }}>
            <div className="an">{t("No match on the map")}</div>
            <div className="aa">
              {t(
                "Check the street spelling, or just type the address and fill in the city, state and zip yourself.",
              )}
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

/** Google's session token: URL-safe and at most 36 characters. */
function newSessionToken(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** One search. A lookup that fails must never block typing an address. */
async function lookup(query: string): Promise<PhotonFeature[]> {
  const params = new URLSearchParams({
    q: query,
    limit: "8",
    lang: "en",
    lat: String(ALABAMA_BIAS.lat),
    lon: String(ALABAMA_BIAS.lon),
    zoom: String(ALABAMA_BIAS.zoom),
    location_bias_scale: String(ALABAMA_BIAS.location_bias_scale),
  });

  try {
    const response = await fetch(`${PHOTON_URL}?${params.toString()}`);
    const body = (await response.json()) as { features?: PhotonFeature[] };
    return body.features ?? [];
  } catch {
    return [];
  }
}

interface PhotonFeature {
  properties: {
    street?: string;
    name?: string;
    housenumber?: string;
    city?: string;
    town?: string;
    village?: string;
    district?: string;
    state?: string;
    postcode?: string;
  };
  geometry?: { coordinates?: [number, number] };
}

export function toSuggestions(
  features: PhotonFeature[],
  query: string,
): Suggestion[] {
  const typedHouseNumber = /^\s*(\d+)\s+(.+)/.exec(query)?.[1] ?? null;
  const seen = new Set<string>();
  const out: Suggestion[] = [];

  for (const feature of features) {
    const p = feature.properties;
    const streetName = p.street ?? p.name;
    if (!streetName) continue;

    const hasNumber = Boolean(p.housenumber);
    const number = hasNumber ? p.housenumber : (typedHouseNumber ?? "");
    const address = [number, streetName].filter(Boolean).join(" ");
    // County was in this list and should not have been. Photon leaves `city`
    // off a rural street and offers the county instead — McMillan Creek Drive
    // comes back with county "Santa Rosa" when the town is Milton — so the
    // fallback quietly typed the wrong city onto the contract. A blank city is
    // filled in by the rep in three seconds; a wrong one is read by nobody.
    const city = p.city ?? p.town ?? p.village ?? p.district ?? "";
    const state = (p.state && US_STATES[p.state]) ?? p.state ?? "";
    const zip = p.postcode ?? "";

    const key = `${address}|${city}|${zip}`.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    // A street-only match carries the *street's* coordinates — for Samuel
    // Sandoval's 626 Valleyview Drive the pin landed on house 704, and Street
    // View trusted it. No pin is better: streetViewUrl falls back to searching
    // Google Maps by the typed address, and Google does know house numbers.
    out.push({
      address,
      city,
      state,
      zip,
      lat: hasNumber ? (feature.geometry?.coordinates?.[1] ?? null) : null,
      lng: hasNumber ? (feature.geometry?.coordinates?.[0] ?? null) : null,
      approximate: !hasNumber && Boolean(typedHouseNumber),
    });
  }
  return out;
}
